/**
 * TicketFieldsPicker
 * Chooses the values to write into a helpdesk's ticket fields.
 *
 * Controlled and data-agnostic: it is handed the helpdesk's field definitions and the values chosen
 * so far, and reports the full next list on every change. It neither fetches nor saves, so any
 * feature that sets ticket fields can compose it with its own data.
 *
 * Required fields are always listed. An optional field is listed once it holds a value, or once it
 * is picked from the "Add an optional field" menu at the bottom.
 */

import * as React from 'react';
import { Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { HelpdeskTicketField, TicketFieldValue } from '@/types';

type FieldValue = TicketFieldValue['value'];

export interface TicketFieldsPickerProps {
  /** The helpdesk's ticket fields a value can be set on. */
  fields: HelpdeskTicketField[];
  /** The values chosen so far, at most one per field. */
  value: TicketFieldValue[];
  /** Called with the full next list whenever a value is set, changed or removed. */
  onChange: (next: TicketFieldValue[]) => void;
  disabled?: boolean;
}

const CONTROL_WIDTH = 'w-56';
const STALE = 'stale';

const choiceLabel = (choice: FieldValue): string =>
  typeof choice === 'boolean' ? (choice ? 'Yes' : 'No') : String(choice);

interface FieldControlProps {
  field: HelpdeskTicketField;
  value: FieldValue | undefined;
  disabled?: boolean;
  /** `undefined` clears the field. */
  onCommit: (value: FieldValue | undefined) => void;
}

/** The input for one field, in the field's own type: its choices, a number, or text. */
function FieldControl({ field, value, disabled, onCommit }: FieldControlProps) {
  if (field.choices.length > 0) {
    const selected = value === undefined ? -1 : field.choices.indexOf(value);
    // A saved value the helpdesk no longer offers stays visible rather than reading as unset.
    const isStale = value !== undefined && selected === -1;
    return (
      <Select
        value={isStale ? STALE : selected === -1 ? '' : String(selected)}
        onValueChange={(next) => next !== STALE && onCommit(field.choices[Number(next)])}
        disabled={disabled}
      >
        <SelectTrigger aria-label={field.label} className={cn(CONTROL_WIDTH, 'text-sm')}>
          <SelectValue placeholder='Select a value' />
        </SelectTrigger>
        <SelectContent align='end'>
          {isStale && (
            <SelectItem value={STALE} disabled>
              {choiceLabel(value)} (no longer offered)
            </SelectItem>
          )}
          {field.choices.map((choice, index) => (
            <SelectItem key={String(choice)} value={String(index)}>
              {choiceLabel(choice)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const isNumber = field.type === 'number';
  const commit = (raw: string) => {
    const text = raw.trim();
    const next = text === '' ? undefined : isNumber ? Number(text) : text;
    if (next !== value && !Number.isNaN(next)) onCommit(next);
  };

  // Uncontrolled, and keyed on the chosen value so it resets when that changes. Committed on blur or Enter.
  return (
    <Input
      key={String(value ?? '')}
      type={isNumber ? 'number' : 'text'}
      aria-label={field.label}
      defaultValue={value === undefined ? '' : String(value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      placeholder={isNumber ? 'Enter a number' : 'Enter a value'}
      disabled={disabled}
      className={cn(CONTROL_WIDTH, 'h-9 text-sm')}
    />
  );
}

function RemoveButton({ label, disabled, onClick }: { label: string; disabled?: boolean; onClick: () => void }) {
  return (
    <Button
      type='button'
      variant='ghost'
      size='icon'
      aria-label={`Remove ${label}`}
      onClick={onClick}
      disabled={disabled}
      className='size-8 shrink-0 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground'
    >
      <X className='size-4' />
    </Button>
  );
}

interface FieldRowProps {
  label: React.ReactNode;
  badge?: React.ReactNode;
  hint?: React.ReactNode;
  control?: React.ReactNode;
  /** Shown in the trailing slot, which is kept for every row so the controls line up. */
  action?: React.ReactNode;
}

function FieldRow({ label, badge, hint, control, action }: FieldRowProps) {
  return (
    <div className='flex items-center justify-between gap-4 py-2'>
      <div className='flex min-w-0 flex-col gap-0.5'>
        <div className='flex min-w-0 items-center gap-2'>
          <span className='truncate text-sm font-medium text-foreground'>{label}</span>
          {badge}
        </div>
        {hint}
      </div>
      <div className='flex shrink-0 items-center gap-1'>
        {control}
        <div className='flex size-8 shrink-0 items-center justify-center'>{action}</div>
      </div>
    </div>
  );
}

export function TicketFieldsPicker({ fields, value, onChange, disabled }: TicketFieldsPickerProps) {
  // Optional fields added from the menu that hold no value yet.
  const [added, setAdded] = React.useState<string[]>([]);

  const valueOf = (fieldId: string) => value.find((entry) => entry.fieldId === fieldId)?.value;

  const commit = (fieldId: string, next: FieldValue | undefined) => {
    const others = value.filter((entry) => entry.fieldId !== fieldId);
    onChange(next === undefined ? others : [...others, { fieldId, value: next }]);
  };

  const remove = (fieldId: string) => {
    setAdded((current) => current.filter((id) => id !== fieldId));
    if (valueOf(fieldId) !== undefined) commit(fieldId, undefined);
  };

  const listed = fields.filter((field) => field.required || valueOf(field.id) !== undefined || added.includes(field.id));
  const addable = fields.filter((field) => !listed.includes(field));
  // Values for fields the helpdesk no longer has: shown so they can be removed, never hidden.
  const orphaned = value.filter((entry) => !fields.some((field) => field.id === entry.fieldId));

  return (
    <div className='flex flex-col'>
      <div className='flex flex-col divide-y divide-border'>
        {listed.map((field) => {
          const missing = field.required && valueOf(field.id) === undefined;
          return (
            <FieldRow
              key={field.id}
              label={field.label}
              badge={
                <Badge variant={field.required ? 'warning' : 'secondary'} className='shrink-0'>
                  {field.required ? 'Required' : 'Optional'}
                </Badge>
              }
              hint={missing && <span className='text-xs text-destructive'>Needs a value</span>}
              control={
                <FieldControl
                  field={field}
                  value={valueOf(field.id)}
                  disabled={disabled}
                  onCommit={(next) => commit(field.id, next)}
                />
              }
              action={!field.required && <RemoveButton label={field.label} disabled={disabled} onClick={() => remove(field.id)} />}
            />
          );
        })}

        {orphaned.map((entry) => (
          <FieldRow
            key={entry.fieldId}
            label={`Field ${entry.fieldId}`}
            hint={<span className='text-xs text-destructive'>No longer available in the helpdesk</span>}
            action={<RemoveButton label={`field ${entry.fieldId}`} disabled={disabled} onClick={() => remove(entry.fieldId)} />}
          />
        ))}
      </div>

      {addable.length > 0 && (
        // Keyed on the list so it resets to its placeholder once a field is added.
        <Select
          key={added.join(',')}
          onValueChange={(fieldId) => setAdded((current) => [...current, fieldId])}
          disabled={disabled}
        >
          <SelectTrigger
            aria-label='Add an optional field'
            size='sm'
            className='mt-2 w-fit gap-1.5 rounded-full border-dashed bg-transparent px-3 text-sm text-muted-foreground hover:text-foreground [&>[data-slot=select-chevron]]:hidden'
          >
            <Plus className='size-3.5' />
            <SelectValue placeholder='Add an optional field' />
          </SelectTrigger>
          <SelectContent align='start'>
            {addable.map((field) => (
              <SelectItem key={field.id} value={field.id}>
                {field.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
